import { debounce } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  RestaurantAutocompleteOption,
  fetchRestaurantAutocomplete,
} from '../services/searchService'

// Phase 2 (search overhaul): the restaurant-owner dashboard typeahead no longer queries Elastic
// directly from the browser (no more REACT_APP_ELASTIC_API_KEY / ElasticsearchAPIConnector). It now
// calls the resactly-service `/search` proxy (REACT_APP_SERVICE_URL, restricted to the restaurant
// index) which forwards to resactly-dis. Callers still receive `{ results, loading }` with the same
// `.raw`-wrapped option shape they rendered before.
const useElasticRestaurantSearch = (
  searchInputValue: string,
): { results: RestaurantAutocompleteOption[]; loading: boolean } => {
  const [results, setResults] = useState<RestaurantAutocompleteOption[]>([])
  const [loading, setLoading] = useState(false)
  // Monotonic request id — ignore any callback that isn't the latest in-flight search so a slow
  // early keystroke can't overwrite a faster later one (stale-response race).
  const requestSeqRef = useRef(0)

  const fetchResults = useMemo(
    () =>
      debounce(
        (
          term: string,
          onResults: (options: RestaurantAutocompleteOption[]) => void,
        ) => {
          void (async () => {
            try {
              const options = await fetchRestaurantAutocomplete({ q: term })
              onResults(options)
            } catch {
              onResults([])
            }
          })()
        },
        1000,
      ),
    [],
  )

  useEffect(() => {
    if (!searchInputValue) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    const reqId = ++requestSeqRef.current
    fetchResults(searchInputValue, (options) => {
      if (reqId !== requestSeqRef.current) return // a newer keystroke superseded this one
      setResults(options)
      setLoading(false)
    })
  }, [searchInputValue, fetchResults])

  return { results, loading }
}

export default useElasticRestaurantSearch
